
import { useState, useEffect } from 'react';
import BlogEditor from '@/components/admin/BlogEditor';
import { WizardBlogGenerator } from '@/components/admin/WizardBlogGenerator';
import { EnhancedBlogGenerator } from '@/components/admin/EnhancedBlogGenerator';
import { useParams, useNavigate } from 'react-router-dom';
import { getBlogPostBySlug } from '@/utils/blogUtils';
import { BlogPost } from '@/types/blog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@/components/ui/breadcrumb';
import { AlertCircle, ChevronLeft, FilePenLine, Sparkles, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EditBlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("editor");
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadPost = async () => {
      if (!slug || slug === 'new') {
        setLoading(false);
        return;
      }
      
      try {
        const fetchedPost = await getBlogPostBySlug(slug, false);
        console.log("Loaded post with author:", fetchedPost?.author);
        setPost(fetchedPost);
        setError(null);
      } catch (error) {
        console.error("Error loading blog post:", error);
        setError("Failed to load blog post. Please try again or contact support.");
      } finally {
        setLoading(false);
      }
    };
    
    loadPost();
  }, [slug]);
  
  const handleBlogGenerated = (generatedPost: Partial<BlogPost>) => {
    console.log("Generated post data received:", generatedPost);
    console.log("Author in generated post:", generatedPost.author);
    
    setPost(prevPost => {
      // Ensure we have correct author handling
      const author = generatedPost.author || prevPost?.author || 'edgefi team';
      console.log("Setting author to:", author);
      
      const updatedPost = {
        ...prevPost,
        id: prevPost?.id,
        slug: generatedPost.slug || prevPost?.slug || '',
        title: generatedPost.title || prevPost?.title || '',
        excerpt: generatedPost.excerpt || prevPost?.excerpt || '',
        content: generatedPost.content || prevPost?.content || '',
        date: generatedPost.date || prevPost?.date || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        coverImage: generatedPost.coverImage || prevPost?.coverImage || '',
        tags: generatedPost.tags || prevPost?.tags || [],
        imageAttribution: generatedPost.imageAttribution || prevPost?.imageAttribution,
        author: author, // Explicitly set the author
      } as BlogPost;
      
      console.log("Updated post with author:", updatedPost.author);
      return updatedPost;
    });
    
    setActiveTab("editor");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-12 h-12 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbItem>
          <BreadcrumbLink href="/admin">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink href="/admin/blog">Blog</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          {slug === 'new' ? 'New Post' : post?.title || slug}
        </BreadcrumbItem>
      </Breadcrumb>
      
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => navigate('/admin/blog')}
        className="mb-2 -ml-2"
      >
        <ChevronLeft size={16} className="mr-1" /> Back to Blog Posts
      </Button>

      {!post && slug === 'new' && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Blog Post</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="editor" className="w-full" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="editor" className="flex items-center gap-2">
                  <FilePenLine size={16} />
                  <span>Manual Editor</span>
                </TabsTrigger>
                <TabsTrigger value="wizard" className="flex items-center gap-2">
                  <Sparkles size={16} />
                  <span>Blog Wizard</span>
                </TabsTrigger>
                <TabsTrigger value="quick" className="flex items-center gap-2">
                  <Zap size={16} />
                  <span>Quick Generator</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="editor">
                <BlogEditor initialPost={post} />
              </TabsContent>
              
              <TabsContent value="wizard">
                <WizardBlogGenerator onBlogGenerated={handleBlogGenerated} />
              </TabsContent>
              
              <TabsContent value="quick">
                <EnhancedBlogGenerator onBlogGenerated={handleBlogGenerated} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
      
      {(post || slug !== 'new') && (
        <BlogEditor initialPost={post} />
      )}
    </div>
  );
};

export default EditBlogPost;
